import React, { useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSnackbar } from 'material-ui-snackbar-provider';

import { ContainedButton } from '../../modules/Buttons';
import styles from './DeletedVideofeedsModal.module.scss';
import Modal from '../../modules/Modal';
import { RolesLowerCase, VIDEOFEED, VIDEOFEED_TYPE } from '../../../enums';
import { findSearchValue, secondsToHours } from '../../../helpers';
import {
  getListArchivedVideofeedsByFolder,
  restoreArchivedVideofeed,
} from '../../../api';
import { Tooltip } from '@material-ui/core';
import { convertSeconds } from '../../Timeline/utils';
import { useAppSelector } from '../../../store/hooks';
import FullPageLoader from '../../modules/FullPageLoader';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as TimelineIcon } from '../../../assets/icons/timeline.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';

type TDeletedVideofeedsModal = {
  open: boolean;
  switchModal: () => void;
  onRestored?: () => void | Promise<void>;
};

const RESTORE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

const formatDate = (iso?: string | Date) => {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} · ${String(
    d.getHours(),
  ).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const isRestoreExpired = (iso?: string | Date) => {
  if (!iso) return true;
  const archivedAt = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(archivedAt.getTime())) return true;
  return Date.now() - archivedAt.getTime() > RESTORE_RETENTION_MS;
};

const isComposerVideofeed = (videofeed: VIDEOFEED) =>
  videofeed?.type === VIDEOFEED_TYPE.COMPOSER || videofeed?.type === VIDEOFEED_TYPE.MY_COMPOSER;

const getCollectionRestoreOrder = (items: VIDEOFEED[]) =>
  [...items].sort((a: any, b: any) => {
    const aIsRoot = !Number(a?.parent_videofeed_id || 0);
    const bIsRoot = !Number(b?.parent_videofeed_id || 0);

    if (aIsRoot !== bIsRoot) return aIsRoot ? -1 : 1;

    return Number(a?.id || 0) - Number(b?.id || 0);
  });

const DeletedVideofeedsModal: React.FC<TDeletedVideofeedsModal> = ({ open, switchModal, onRestored }) => {
  const [loading, setLoading] = useState(false);
  const [deletedArr, setDeletedArr] = useState<VIDEOFEED[]>([]);
  const [periodOnly, setPeriodOnly] = useState(true);
  const [restoringKey, setRestoringKey] = useState<number | string | null>(null);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const folderId = findSearchValue('folderId');
  const notification = useSnackbar();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const canRestoreByRole = [RolesLowerCase.ORGANIZATION_ADMIN, RolesLowerCase.ANALYST, RolesLowerCase.SUPER_ADMIN].includes(
    (currentUser?.role?.title as RolesLowerCase) || ('' as RolesLowerCase),
  );
  const columnsCount = canRestoreByRole ? 7 : 6;
  const restoreInProgress = restoringKey !== null;

  useHotkeys('Enter', () => {
    if (open && !loading && !restoreInProgress) submitButtonRef?.current?.click();
  });

  useEffect(() => {
    if (open && folderId) void fetchDeletedVideofeeds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folderId, periodOnly]);

  const fetchDeletedVideofeeds = async () => {
    try {
      setLoading(true);
      const res = await getListArchivedVideofeedsByFolder(folderId!, periodOnly);
      if (res.data) setDeletedArr(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalSeconds = deletedArr.reduce((sum, v: any) => sum + Number(v?.duration || 0), 0);
  const totalHoursLabel = secondsToHours(totalSeconds || 0);
  const collectionCounts = deletedArr.reduce((acc, item: any) => {
    const key = Number(item?.collection_root_id || 0);
    if (key > 0) {
      acc.set(key, (acc.get(key) || 0) + 1);
    }
    return acc;
  }, new Map<number, number>());
  const groupedDeletedArr = deletedArr.reduce(
    (acc, item: any) => {
      const collectionRootId = Number(item?.collection_root_id || 0);
      const hasArchivedCollectionPeers = collectionRootId > 0 && (collectionCounts.get(collectionRootId) || 0) > 1;
      const isCollectionItem = Number(item?.parent_videofeed_id || 0) > 0 || hasArchivedCollectionPeers;
      const groupKey = isCollectionItem ? `collection-${collectionRootId || item.id}` : `videofeed-${item.id}`;

      let group = acc.find((entry) => entry.key === groupKey);
      if (!group) {
        group = {
          key: groupKey,
          isCollection: isCollectionItem,
          title: item?.collection_title || item?.collection_name || item?.title || 'Movie Collection',
          items: [],
        };
        acc.push(group);
      }

      group.items.push(item);
      return acc;
    },
    [] as Array<{
      key: string;
      isCollection: boolean;
      title: string;
      items: VIDEOFEED[];
    }>,
  );

  const onRestore = async (videofeedId: number) => {
    if (restoreInProgress) return;

    try {
      setRestoringKey(videofeedId);
      await restoreArchivedVideofeed(videofeedId);
      notification.showMessage('Videofeed restored');
      if (onRestored) {
        await Promise.resolve(onRestored());
      }
      switchModal();
    } catch (err: any) {
      console.error(err);
      if (err?.response?.data?.message) {
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('Failed to restore videofeed');
      }
    } finally {
      setRestoringKey(null);
    }
  };

  const onRestoreCollection = async (groupKey: string, videofeeds: VIDEOFEED[]) => {
    if (restoreInProgress) return;

    try {
      setRestoringKey(groupKey);
      let restoredCount = 0;
      let failedCount = 0;

      for (const videofeed of getCollectionRestoreOrder(videofeeds)) {
        if (Number(videofeed?.id || 0) > 0) {
          try {
            await restoreArchivedVideofeed(Number(videofeed.id));
            restoredCount += 1;
          } catch (err) {
            console.error(err);
            failedCount += 1;
          }
        }
      }

      if (restoredCount > 0) {
        if (onRestored) {
          await Promise.resolve(onRestored());
        }
      }

      if (failedCount > 0) {
        notification.showMessage(
          failedCount === 1
            ? '1 videofeed could not be restored'
            : `${failedCount} videofeeds could not be restored`,
        );

        if (restoredCount > 0) {
          await fetchDeletedVideofeeds();
        }

        return;
      }

      if (restoredCount > 0) {
        notification.showMessage('Movie collection restored');
        switchModal();
        return;
      }

      notification.showMessage('Failed to restore movie collection');
    } catch (err: any) {
      console.error(err);
      if (err?.response?.data?.message) {
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('Failed to restore movie collection');
      }
    } finally {
      setRestoringKey(null);
    }
  };

  const renderVideofeedTypeIcon = (videofeed: VIDEOFEED) => {
    if (isComposerVideofeed(videofeed)) {
      return <ComposerIcon className={`${styles.typeIcon} ${styles.composerIcon}`} />;
    }

    if ((videofeed as any)?.duplicate) {
      return <TimelineIcon className={`${styles.typeIcon} ${styles.timelineIcon}`} />;
    }

    return <MovieIcon className={`${styles.typeIcon} ${styles.collectionIcon}`} />;
  };

  return (
    <>
      <FullPageLoader loading={restoreInProgress} />
      <Modal open={open} onClose={restoreInProgress ? undefined : switchModal} loading={loading}>
        <div className={styles.modalBody}>
          <div className={styles.headerBlock}>
            <div className={styles.modalEyebrow}>Archive</div>
            <div className={styles.modalTitle}>
              Deleted videofeeds
              {!!deletedArr.length && <span className={styles.count}>{deletedArr.length}</span>}
            </div>
            <div className={styles.modalSubtitle}>
              Restore a single videofeed or bring back an entire archived movie collection with all of its timelines.
            </div>
          </div>

          <div className={styles.toolbar}>
            <label className={styles.periodFilter}>
              <input
                type="checkbox"
                checked={periodOnly}
                onChange={(e) => setPeriodOnly(e.target.checked)}
                disabled={loading || restoreInProgress}
              />
              Only this period
            </label>

            <div className={styles.summaryCard}>
              <span>Total hours</span>
              <Tooltip title={convertSeconds(totalSeconds || 0, false, true)}>
                <strong>{totalHoursLabel}</strong>
              </Tooltip>
            </div>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.titleColumn}>Title</th>
                  <th className={styles.preDeleteColumn}>Pre-delete</th>
                  <th className={styles.dateColumn}>Created at</th>
                  <th className={styles.dateColumn}>Deleted at</th>
                  <th className={styles.hoursColumn}>Upload Hours</th>
                  <th className={styles.idColumn}>ID</th>
                  {canRestoreByRole && <th className={styles.actionsColumn}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className={styles.skeletonRow}>
                      <td colSpan={columnsCount}></td>
                    </tr>
                  ))
                ) : deletedArr.length === 0 ? (
                  <tr>
                    <td colSpan={columnsCount} className={styles.emptyCell}>
                      No deleted videofeeds in this folder
                    </td>
                  </tr>
                ) : (
                  groupedDeletedArr.map((group) => (
                    <React.Fragment key={group.key}>
                      {group.isCollection && (
                        <tr className={styles.collectionRow}>
                          <td colSpan={columnsCount}>
                            <div className={styles.collectionHeader}>
                              <div className={styles.collectionMeta}>
                                <MovieIcon className={`${styles.typeIcon} ${styles.collectionHeaderIcon}`} />
                                <Tooltip title={group.title || ''}>
                                  <span className={styles.collectionTitle}>{group.title}</span>
                                </Tooltip>
                                <span className={styles.collectionCount}>{group.items.length}</span>
                              </div>
                              {canRestoreByRole && (
                                <button
                                  type="button"
                                  className={styles.restoreBtn}
                                  onClick={() => {
                                    if (
                                      restoreInProgress ||
                                      group.items.some((item: any) => isRestoreExpired(item.deleted_at || item.updated_at))
                                    ) {
                                      return;
                                    }
                                    onRestoreCollection(group.key, group.items);
                                  }}
                                  disabled={
                                    loading ||
                                    restoreInProgress ||
                                    !group.items.length ||
                                    group.items.some((item: any) => isRestoreExpired(item.deleted_at || item.updated_at))
                                  }
                                >
                                  {group.items.some((item: any) => isRestoreExpired(item.deleted_at || item.updated_at))
                                    ? 'Expired'
                                    : restoringKey === group.key
                                      ? 'Restoring...'
                                      : 'Restore entire collection'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      {group.items.map((v: any, index: number) => {
                        const restoreExpired = isRestoreExpired(v.deleted_at || v.updated_at);

                        return (
                          <tr
                            key={v.id}
                            className={[
                              group.isCollection ? styles.collectionItemRow : '',
                              group.isCollection && index === group.items.length - 1 ? styles.collectionItemLastRow : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <td
                              className={[
                                styles.titleCell,
                                group.isCollection ? styles.collectionTitleCell : '',
                                group.isCollection && index === group.items.length - 1 ? styles.collectionTitleCellLast : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            >
                              <div className={styles.titleContent}>
                                {renderVideofeedTypeIcon(v)}
                                <Tooltip title={v.title || ''}>
                                  <span className={styles.titleText}>{v.title}</span>
                                </Tooltip>
                              </div>
                            </td>
                            <td>{v.pre_delete_status || '—'}</td>
                            <td>{formatDate(v.created_at)}</td>
                            <td>{formatDate(v.deleted_at || v.updated_at)}</td>
                            <td>
                              <div className={styles.hoursBadge}>
                                {v?.duration ? (
                                  <Tooltip title={convertSeconds(v.duration, false, true)}>
                                    <span>{secondsToHours(v.duration)}</span>
                                  </Tooltip>
                                ) : (
                                  <span>—</span>
                                )}
                                {v.duplicate && <span className={styles.badgeBlue}>REF</span>}
                              </div>
                            </td>
                            <td className={styles.idCell}>{v.id}</td>
                            {canRestoreByRole && (
                              <td>
                                <button
                                  type="button"
                                  className={styles.restoreBtn}
                                  onClick={() => {
                                    if (restoreExpired || restoreInProgress) return;
                                    onRestore(Number(v.id));
                                  }}
                                  disabled={loading || restoreInProgress || restoreExpired}
                                >
                                  {restoreExpired ? 'Expired' : restoringKey === Number(v.id) ? 'Restoring...' : 'Restore'}
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                      {group.isCollection && (
                        <tr className={styles.collectionSpacer} aria-hidden="true">
                          <td colSpan={columnsCount}></td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.actions}>
            <ContainedButton onClick={switchModal} disabled={loading || restoreInProgress} kind="purple">
              <div ref={submitButtonRef}>Close</div>
            </ContainedButton>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DeletedVideofeedsModal;
